import { z } from 'zod';

/**
 * A `userLocation` grants one `user` access to one `location`. A user may be
 * assigned to many locations (staff sometimes work across several parks), and a
 * location has many users.
 *
 * An assignment is the ONLY thing that grants a standard user access — absence
 * means no access (fail closed). `superAdmin`s are never assigned; they access
 * every location without a row (see `canAccessLocation` in `convex/utils/withAuth.ts`).
 *
 * Assignments live in Convex rather than in a Cognito `custom:` attribute because:
 * - Revoking access must bite on the next query. Any claim baked into a JWT is
 *   stale until the token turns over, so unassignment needs a server-side read.
 * - Cognito cannot filter `ListUsers` by a custom attribute, so "which users are
 *   at this location?" would be unbuildable.
 * - Custom attributes are capped strings with no foreign keys and no cascade,
 *   and writing one would make every assignment a two-system sync.
 */
export const userLocationSchema = z.object({
  /** References `user._id`. */
  userId: z.string(),
  /** References `location._id`. */
  locationId: z.string(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type UserLocation = z.output<typeof userLocationSchema>;

export const userLocationCvxSchema = userLocationSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type UserLocationCvx = z.output<typeof userLocationCvxSchema>;
